import { Text } from "@hackthenorth/north";
import React from "react";
import styled from "styled-components";

import { User } from "src/api/types.generated";
import { mediaQueries } from "src/theme/deviceBreakpoints";
import { resolvePal } from "src/views/goose-games/hackPals";
import { GGPlayer } from "src/views/goose-games/types";

import { BalanceChip } from "../../balanceChip";
import HackPalBanner from "../../hackPalBanner";

interface Props {
  profile?: Pick<User, "name">;
  palNumber?: number;
  ggPlayer?: GGPlayer;
  stackDetails?: boolean;
}

const ProfileCard = ({
  profile,
  palNumber,
  ggPlayer,
  stackDetails = false,
}: Props) => {
  const totalBalance = ggPlayer?.total_points ?? 0;

  return (
    <CardWrapper $hugContent={stackDetails}>
      <PositionWrapper>
        <HackPalBanner
          balance={totalBalance}
          hackpal={resolvePal(palNumber).image}
          showBackground={false}
          showForegroundBanner={false}
        />
      </PositionWrapper>
      <Card $hugContent={stackDetails}>
        <ContentWrapper $hugContent={stackDetails}>
          <FlexHeader $hugContent={stackDetails}>
            <DetailsWrapper $stackDetails={stackDetails}>
              <StyledTitle
                mods="heading title"
                length={profile ? profile.name.length : 0}
              >
                {profile ? profile.name : ""}
              </StyledTitle>
              <BalanceChip points={ggPlayer ? ggPlayer.current_points : 0} />
            </DetailsWrapper>
          </FlexHeader>
        </ContentWrapper>
      </Card>
    </CardWrapper>
  );
};

const CardWrapper = styled.div<{ $hugContent: boolean }>`
  --avatar-size: min(20vw, 72px);
  --avatar-overlap: min(10vw, 36px);

  position: relative;
  display: flex;
  align-items: center;
  width: ${({ $hugContent }) => ($hugContent ? "fit-content" : "100%")};
  max-width: 100%;
`;

const Card = styled.div<{ $hugContent: boolean }>`
  position: relative;
  display: flex;
  width: ${({ $hugContent }) =>
    $hugContent ? "fit-content" : "calc(100% - var(--avatar-overlap))"};
  max-width: 100%;
  min-height: calc(var(--avatar-size) - 8px);
  margin-left: var(--avatar-overlap);
  flex-direction: column;
  align-items: start;
  justify-content: center;
  gap: 4px;
  border-radius: 8px;
  border: 1px solid var(--North-Border-Gray, #d1d5db);
  background: ${({ theme }) => theme.globalConstants.color.white};
  padding: ${({ $hugContent }) => ($hugContent ? "8px 16px" : "0 14px 0 0")};
  padding-left: calc(var(--avatar-overlap) + 8px);
`;

const StyledTitle = styled(Text)<{ length: number }>`
  font-size: ${({ length }) =>
    length > 10 ? "18px !important" : "22px !important"};
  line-height: normal !important;
  margin-bottom: 0px !important;
  overflow-x: hidden;
  text-overflow: ellipsis;
`;

const PositionWrapper = styled.div`
  position: absolute;
  z-index: 1;
  top: 50%;
  right: calc(100% - var(--avatar-overlap) - var(--avatar-overlap));
  height: calc(100% + 24px);
  aspect-ratio: 1;
  transform: translateY(-50%);
`;

const ContentWrapper = styled.div<{ $hugContent: boolean }>`
  display: flex;
  width: ${({ $hugContent }) => ($hugContent ? "auto" : "100%")};
  min-width: 0;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
`;

const FlexHeader = styled.div<{ $hugContent: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: ${({ $hugContent }) => ($hugContent ? "auto" : "100%")};
  gap: 8px;

  ${mediaQueries.medium} {
    flex-direction: column;
    gap: 24px;
  }
`;

const DetailsWrapper = styled.div<{ $stackDetails: boolean }>`
  display: flex;
  flex-direction: ${({ $stackDetails }) => ($stackDetails ? "column" : "row")};
  justify-content: ${({ $stackDetails }) =>
    $stackDetails ? "center" : "space-between"};
  align-items: ${({ $stackDetails }) =>
    $stackDetails ? "flex-start" : "center"};
  gap: 8px;
  overflow: hidden;
  width: ${({ $stackDetails }) => ($stackDetails ? "auto" : "100%")};
`;

export default ProfileCard;
